/* The structured block Brill emits when she drafts a website section beside the page.
 *
 * WHY THIS ISN'T PROSE — the same reason the Offer Ladder isn't.
 * Reading fields back out of Brill's paragraphs produced three separate
 * wrong-data bugs in `offerStore.js` (a cost-of-pain figure read as a billing
 * interval, a label stored as an offer name, a payment plan missed entirely),
 * because the layout is a guideline Brill follows loosely, not a contract. So
 * nothing here is inferred. Brill states each field on its own labelled line and
 * we read exactly those; anything she can't state, she omits, and the field stays
 * empty for the coach to fill.
 *
 * WHY IT'S DRIVEN BY THE KIND SPEC
 * A section's fields differ by kind, so this parser has no field list of its own —
 * it reads `websiteSectionKinds.js`, which is also what the editor renders and
 * what gets sent to Brill in [SECTION_CONTEXT]. One definition, three consumers.
 * A field added there needs no change here.
 *
 *   <<<SECTION_DRAFTED>>>
 *   EYEBROW: Tevra Wood Consulting
 *   HEAD: Build a business that fits how you're wired.
 *   SUB: Coaching for people who've outgrown advice built for everyone else.
 *   BTN: Take the archetype assessment
 *   <<<SECTION_END>>>
 *
 * List fields repeat one label, parts separated by " | ":
 *
 *   ITEM: Archetype Assessment | Find your energy instincts. | Free
 *   ITEM: Business Blueprint | A full aligned blueprint, built with you. | $4,800
 */

import { blockLabel, fieldsOf, isListField, kindDef, rowsOf, sectionSpecLines } from "./websiteSectionKinds";

const BLOCK = /<<<SECTION_DRAFTED>>>([\s\S]*?)<<<SECTION_END>>>/;

/* A label line: optional markdown decoration, the label, a colon, the value.
   Brill wraps labels in bold often enough (`**HEAD:**`) that not allowing for it
   would drop whole fields. */
const LABEL_LINE = /^[ \t]*[*#>\-\s]*([A-Z_]{2,})[ \t]*\*{0,2}[ \t]*:[ \t]*(.*)$/;

/* A value that is only a bracketed placeholder is the template coming back —
   `HEAD: [the headline]`. That is not an answer, and storing it would put square
   brackets in her website copy. Treated as omitted. */
const PLACEHOLDER = /^\[[^\]]*\]$/;

/** Does this reply contain a drafted block at all? */
export function hasSectionBlock(text) {
  return BLOCK.test(text || "");
}

/**
 * The labelled entries inside a block, in the order Brill wrote them.
 *
 * Scanned line by line rather than matched per label, because list fields repeat
 * the SAME label and a per-label regex would find only the first ITEM. A value
 * runs until the next label, so a prose field keeps its line breaks.
 *
 * ANY label-shaped line ends the current value, including one this kind doesn't
 * have. Brill keeping a label from another section (`PRICE:` on a banner) is far
 * likelier than a coach's subhead happening to start with a word in capitals and
 * a colon — and treating it as continuation would fold the stray label and its
 * value into the middle of the field above it, where she'd find it in her copy.
 */
function readEntries(body, labels) {
  const known = new Set(labels);
  const entries = [];
  let current = null;

  for (const line of String(body || "").split(/\r?\n/)) {
    const m = LABEL_LINE.exec(line);
    if (m) {
      const label = m[1].toUpperCase();
      current = known.has(label) ? { label, lines: [m[2]] } : null;
      if (current) entries.push(current);
    } else if (current) {
      current.lines.push(line);
    }
    // Lines before the first label are Brill's prose, not a field. Dropped.
  }

  return entries.map((e) => ({
    label: e.label,
    value: e.lines.join("\n").replace(/\*/g, "").trim(),
  }));
}

/** Split one list row on its separator, folding any extra segments back into the
 *  last part. Brill writing a description that itself contains a pipe must not
 *  silently lose the tail of it. */
function splitRow(value, parts) {
  const segments = value.split("|").map((s) => s.trim());
  const row = {};
  parts.forEach((p, i) => {
    const isLast = i === parts.length - 1;
    const raw = isLast ? segments.slice(i).join(" | ") : segments[i];
    const v = String(raw || "").trim();
    row[p.key] = PLACEHOLDER.test(v) ? "" : v;
  });
  return row;
}

/**
 * The fields Brill drafted, as a partial `content` object, or null when there's
 * no block.
 *
 * Partial on purpose: a block with only a HEAD is a usable result. A field Brill
 * omitted is ABSENT from the returned object rather than empty, which is what
 * lets `applySectionBlock` tell "she said nothing" from "she said to clear it".
 */
export function parseSectionBlock(text, kind) {
  return parseLabelledBlock(text, fieldsOf(kind), BLOCK);
}

/**
 * The same reader for ANY field list and any pair of delimiters — what the
 * Outreach drafter reads a touch through (utils/outreach.js). One reader, so a
 * fix to how labels are read lands in every drafting chat at once.
 */
export function parseLabelledBlock(text, fields, re) {
  const m = re.exec(text || "");
  if (!m) return null;

  const byLabel = new Map(fields.map((f) => [blockLabel(f), f]));
  const entries = readEntries(m[1], [...byLabel.keys()]);

  const content = {};
  for (const entry of entries) {
    const field = byLabel.get(entry.label);
    if (!field || !entry.value || PLACEHOLDER.test(entry.value)) continue;

    if (!isListField(field)) {
      // Last one wins: if Brill restates a field inside one block, the correction
      // is the later line.
      content[field.key] = entry.value;
      continue;
    }
    const row = field.parts ? splitRow(entry.value, field.parts) : entry.value;
    const filled = field.parts ? Object.values(row).some(Boolean) : !!row;
    if (!filled) continue;
    content[field.key] = [...(content[field.key] || []), row];
  }
  return content;
}

/**
 * The reply with the block removed — what the coach reads in the chat.
 *
 * The fields are rendered as form inputs beside the conversation, so leaving the
 * block inline would show her the same content twice with delimiters around it.
 */
export function stripSectionBlock(text) {
  return stripLabelledBlock(text, BLOCK);
}

/** The reply with any block matching `re` removed. */
export function stripLabelledBlock(text, re) {
  return (text || "")
    .replace(new RegExp(re.source, "g"), "")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/** Fold a drafted block onto content, for any field list — see applySectionBlock. */
export function applyLabelledBlock(content, block, fields) {
  if (!block) return content || {};
  const next = { ...(content || {}) };
  for (const f of fields || []) {
    const drafted = block[f.key];
    if (drafted === undefined) continue;
    if (isListField(f)) {
      if (drafted.length) next[f.key] = drafted;
      continue;
    }
    if (String(drafted).trim()) next[f.key] = drafted;
  }
  return next;
}

/**
 * Fold a drafted block onto the section being edited.
 *
 * Her own words win over an omission — Brill leaving out SUB must never blank a
 * subhead she already wrote. A list Brill did state REPLACES the stored one
 * rather than appending to it: she asked for three services and got three, so
 * adding them to the three already there would double the section every time she
 * asked for a rewrite.
 */
export function applySectionBlock(content, block, kind) {
  if (!block) return content || {};
  const next = { ...(content || {}) };
  for (const f of fieldsOf(kind)) {
    const drafted = block[f.key];
    if (drafted === undefined) continue;
    if (isListField(f)) {
      if (drafted.length) next[f.key] = drafted;
      continue;
    }
    if (String(drafted).trim()) next[f.key] = drafted;
  }
  return next;
}

/**
 * What Brill is told before she starts drafting: which page, which kind, the
 * exact labels to answer under, and what the coach already has in the section.
 *
 * Sent as its own leading turn rather than folded into the coach's message, the
 * same way the Offer Ladder sends [OFFER_CONTEXT] — the guide reads it as
 * instruction and she never sees it.
 *
 * The FIELD LIST IS BUILT FROM THE SPEC, not written into the prompt on the
 * server. `api/` is CommonJS and can't import the spec, so a hard-coded copy
 * there would drift the first time a field changed. This is the one place the
 * labels exist.
 */
export function buildSectionContext({ page, kind, section, specLines = [], extraLines = [] }) {
  const lines = [
    "[SECTION_CONTEXT — read only, do not reference as a conversation]",
    `Page: ${page?.title || "Untitled page"} (/${page?.slug || ""})`,
    /* What kind of page this is, when the caller knows something the page's own
       name can't say — a funnel step sends the path it sits on (see
       funnelSections.js `stepBrief`). A website page sends nothing here. */
    ...(extraLines || []).filter((l) => String(l || "").trim()),
    `Section kind: ${kind?.label || "Other"} — ${kind?.hint || ""}`,
    "",
    /* WHY THIS LINE TELLS BRILL THE COUNT MATTERS, when the app never enforces
       it. It used to read "Word counts are guidance for whoever builds the page,
       not limits" — true of the APP, and the wrong thing to say to the WRITER.
       That sentence is a protection for her: nothing truncates, nothing blocks a
       save, over budget just goes amber. Brill is not her. Read by the drafter it
       is permission to overshoot, and it was taken: a 6-word eyebrow came back at
       9 (2026-09-15). The room is real even though the app will not police it —
       a template gives you the width it gives you — so the instruction says so,
       and the not-a-limit half stays where it belongs, on the screen. */
    "Fill exactly these fields. Each one says the room it has — write to that word count. It is what the page has space for, and anything over it she has to cut herself:",
    ...specLines,
  ];

  const existing = existingLines(section, kind);
  if (existing.length) {
    lines.push("", "What she already has in this section — edit it, don't start over:", ...existing);
  } else {
    lines.push("", "The section is empty. This is a first draft.");
  }
  return lines.join("\n");
}

/** The section's current content, in the same labelled shape Brill writes back,
 *  so a rewrite is an edit of something rather than a guess at what's there. */
function existingLines(section, kind) {
  const content = section?.content || {};
  const out = [];
  for (const f of (kind?.fields || [])) {
    if (!isListField(f)) {
      const v = String(content[f.key] ?? "").trim();
      if (v) out.push(`${blockLabel(f)}: ${v}`);
      continue;
    }
    for (const row of rowsOf(content, f)) {
      const v = f.parts
        ? f.parts.map((p) => String(row?.[p.key] ?? "").trim()).join(" | ")
        : String(row ?? "").trim();
      if (v.replace(/\|/g, "").trim()) out.push(`${blockLabel(f)}: ${v}`);
    }
  }
  return out;
}


/* ── Labels for a whole RECORD at once ────────────────────────────────────────
   One reply can fill several blocks of the same kind — two text sections on a
   page, two emails in a sequence — so each block's labels carry its PLACE:
   SECTION_TWO_HEAD, TOUCH_THREE_MESSAGE. Words, not digits: the label reader
   above accepts capitals and underscores only, and widening it to digits would
   let a line like "Q3: …" inside an answer end the field early. Shared by
   Sites and Outreach, so both number the same way. */
export const ORDINALS = ["ONE", "TWO", "THREE", "FOUR", "FIVE", "SIX", "SEVEN", "EIGHT", "NINE", "TEN",
  "ELEVEN", "TWELVE", "THIRTEEN", "FOURTEEN", "FIFTEEN", "SIXTEEN", "SEVENTEEN", "EIGHTEEN", "NINETEEN", "TWENTY"];
export const ordinalWord = (i) => ORDINALS[i] || `NUMBER_${ORDINALS[(i % 20)]}_${ORDINALS[Math.floor(i / 20) - 1] || "MORE"}`;


/* ── Writing the WHOLE PAGE with Brill (2026-09-16) ───────────────────────────
   The record scope of the shared drafter on a website page. Same
   <<<SECTION_DRAFTED>>> block as one section; every section's fields are
   labelled with its place on the page. */

/** Every section's fields, labelled by place. `key` is `${sectionId}::${field}`,
 *  which is how a parsed answer finds its section. Parts and type are kept, so
 *  a repeating field still reads as one. */
export function pageFields(sections = []) {
  const out = [];
  (sections || []).forEach((sec, i) => {
    for (const f of fieldsOf(sec?.sectionType)) {
      out.push({ ...f, key: `${sec.id}::${f.key}`, block: `SECTION_${ordinalWord(i)}_${blockLabel(f)}`, sectionId: sec.id, field: f.key });
    }
  });
  return out;
}

/* A page longer than this is written a few sections at a time: a reply
   carrying nine sections runs past what one call can return inside the
   function's time limit, and a block cut off before its end marker reads as
   nothing written. */
export const PAGE_SECTIONS_PER_REPLY = 4;

/** What Brill is told when the conversation is about the WHOLE page. */
export function buildPageContext({ page, sections = [], extraLines = [] }) {
  const lines = [
    "[PAGE_CONTEXT — read only, do not reference as a conversation]",
    `Page: ${page?.title || "Untitled page"} (/${page?.slug || ""})`,
    ...(extraLines || []).filter((l) => String(l || "").trim()),
    `Scope: THE WHOLE PAGE — ${(sections || []).length} sections, top to bottom. Write as many sections as she asks for, each under its own labels, all inside ONE block — but never more than ${PAGE_SECTIONS_PER_REPLY} in one reply. On a longer page, write the first ${PAGE_SECTIONS_PER_REPLY} she has not got yet and say which you will do next.`,
    "Leave out any section you are not changing. Make the page read as one argument, top to bottom — no two sections saying the same thing.",
    "",
  ];
  (sections || []).forEach((sec, i) => {
    const kind = kindDef(sec?.sectionType);
    const fields = fieldsOf(kind.id);
    const place = `SECTION_${ordinalWord(i)}_`;
    lines.push(`— Section ${i + 1} of ${sections.length}: ${kind.label} — ${kind.hint || ""}`);
    /* The kind's own spec lines, one per field in field order, relabelled with
       this section's place. */
    sectionSpecLines(kind.id, sec?.limits).forEach((line, n) => {
      const f = fields[n];
      const label = f ? blockLabel(f) : "";
      lines.push(`  ${f && line.startsWith(`${label}:`) ? place + line : line}`);
    });
    const relabelled = { fields: fields.map((f) => ({ ...f, block: place + blockLabel(f) })) };
    const existing = existingLines(sec, relabelled);
    lines.push(existing.length ? "  What she has now — edit it, don't start over:" : "  Empty — a first draft.");
    for (const l of existing) lines.push(`    ${l}`);
    lines.push("");
  });
  return lines.join("\n");
}

/** { [sectionId]: partial content } for the sections Brill wrote, or null. */
export function parsePageBlock(text, sections = []) {
  const fields = pageFields(sections);
  const flat = parseLabelledBlock(text, fields, BLOCK);
  if (!flat) return null;
  const out = {};
  for (const f of fields) {
    if (flat[f.key] === undefined) continue;
    (out[f.sectionId] = out[f.sectionId] || {})[f.field] = flat[f.key];
  }
  return Object.keys(out).length ? out : null;
}

const CUT_OFF = /<<<SECTION_DRAFTED>>>(?![\s\S]*<<<SECTION_END>>>)[\s\S]*$/;

/** The reply without its block. A block that started and never ended — the
 *  reply ran out of room — is replaced by a sentence that says so, rather
 *  than her reading raw labels and wondering where the draft went. */
export function stripPageBlock(text) {
  const cut = CUT_OFF.test(text || "");
  const clean = stripSectionBlock(String(text || "").replace(CUT_OFF, ""));
  return cut
    ? `${clean ? `${clean}\n\n` : ""}That draft ran longer than one reply can hold, so it didn't come through. Ask me for fewer sections at a time — two or three.`
    : clean;
}
